export const vertex = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * vec4(position, 1.0);
}
`;

export const fragment = /* glsl */ `
uniform float time;
uniform vec4 resolution;
varying vec2 vUv;
uniform sampler2D uTexture;
uniform sampler2D uDisplacement;
uniform vec2 uBgScale;
uniform vec3 uBgFill;
uniform vec2 uTexel;

uniform float uFreq;
uniform float uSpeed;
uniform float uAmp;
uniform vec3  uBaseColor;
uniform vec3  uLightDir;
uniform float uDiffuse;
uniform float uSpecular;
uniform float uShininess;
uniform float uDistortion;

uniform float uRefract;
uniform float uSwirl;
uniform float uRippleLift;
uniform float uChroma;
uniform float uWave3;

uniform vec3  uInkColor;

uniform vec3  uTint;
uniform float uExposure;
uniform float uContrast;
uniform float uSaturation;

float PI = 3.1415926535589703238;

// How much water light the name picks up. 0 keeps the glyphs at the flat nav
// green; 1 lets them take the same lift as the water.
const float INK_RIPPLE = 0.0;

// Glyph coverage from the ink plate. The plate is a black field with the name in
// green, so the green channel alone is the mask. sqrt undoes the sampler's sRGB
// decode, recovering the coverage the artwork baked into its antialiasing; off
// the plate there is no name, so coverage is zero.
float inkCoverage(sampler2D plate, vec2 p) {
  if (p.x < 0.0 || p.x > 1.0 || p.y < 0.0 || p.y > 1.0) return 0.0;
  return sqrt(texture2D(plate, p).g);
}

vec3 bathtubRipples(vec2 uv, float t, vec2 grad) {
  float freq = uFreq;
  float speed = uSpeed;
  float amp = uAmp;
  float phase = t * speed;

  float waveX = sin(uv.x * freq * 6.2831 - phase) +
                sin((1.0 - uv.x) * freq * 6.2831 - phase);
  float waveY = sin(uv.y * freq * 7.2831 - phase) +
                sin((1.0 - uv.y) * freq * 7.2831 - phase);
  // Rotated pair at incommensurate frequencies so the axis-aligned standing
  // waves stop reading as a repeating grid.
  float waveD = sin(dot(uv, vec2(0.707, 0.707)) * freq * 5.317 - phase * 1.13) +
                sin(dot(uv, vec2(-0.707, 0.707)) * freq * 4.731 - phase * 0.87);

  float h = amp * (waveX + waveY + uWave3 * waveD);
  float hx = dFdx(h);
  float hy = dFdy(h);
  // The ripple field's slope tilts the same normal, so interaction/swimmer
  // ripples catch the light exactly like the procedural waves do.
  vec3 normal = normalize(vec3(-(hx + grad.x * uRippleLift), 1.0, -(hy + grad.y * uRippleLift)));

  vec3 lightDir = normalize(uLightDir);
  vec3 viewDir  = normalize(vec3(0.0, 1.0, 0.0));

  float diff = max(dot(normal, lightDir), 0.0);
  vec3 reflectDir = reflect(-lightDir, normal);
  float spec = pow(max(dot(reflectDir, viewDir), 0.0), uShininess);

  return uBaseColor + diff * uDiffuse + spec * uSpecular;
}

void main() {
  // Slope of the ripple render target: refraction pushes the background away
  // from crests like a lens instead of the old brightness-as-angle swirl.
  float d0 = texture2D(uDisplacement, vUv).r;
  float dR = texture2D(uDisplacement, vUv + vec2(uTexel.x, 0.0)).r;
  float dU = texture2D(uDisplacement, vUv + vec2(0.0, uTexel.y)).r;
  vec2 grad = vec2(dR - d0, dU - d0);

  vec2 uv = vUv - grad * uRefract;
  // Legacy swirl kept as an art-direction blend (uSwirl 0 = off).
  float theta = d0 * 2.0 * PI;
  uv += vec2(sin(theta), cos(theta)) * d0 * uSwirl;
  uv.x += sin(time + uv.y) * uDistortion;
  uv.y += cos(time + uv.x) * uDistortion;

  vec2 bgUv = (uv - 0.5) * uBgScale + 0.5;
  // Wet-lens chroma: coverage read three times a hair apart along the slope, so
  // R/G/B cross the glyph edge at slightly different places. Same three fetches
  // the plate sample used to cost.
  vec3 ink = vec3(
    inkCoverage(uTexture, bgUv - grad * uChroma),
    inkCoverage(uTexture, bgUv),
    inkCoverage(uTexture, bgUv + grad * uChroma)
  );

  vec3 ripple = bathtubRipples(uv, time, grad);
  // Blend between the two FINISHED colours - lit water and flat nav green -
  // rather than shading a pre-composited plate. That is what kills the dark
  // outline: mix() can only land between its endpoints, so a half-covered edge
  // pixel cannot dip darker than both of its neighbours.
  vec3 water = uBgFill + ripple;
  vec3 name  = uInkColor + ripple * INK_RIPPLE;
  vec3 mixed = mix(water, name, ink);
  gl_FragColor = vec4(mixed, 1.0);

  vec3 c = gl_FragColor.rgb * uExposure * uTint;
  float luma = dot(c, vec3(0.2126, 0.7152, 0.0722));
  c = mix(vec3(luma), c, uSaturation);
  c = (c - 0.5) * uContrast + 0.5;
  gl_FragColor = vec4(c, gl_FragColor.a);
}
`;
